<template>
  <DetailsAsideLayout>
    <template v-slot:detailsContent>
      <div :class="$style.sectionRoot">
        <div :class="$style.detailsSection">
          <ul :class="$style.appDetails">
            <li
              v-for="detail in appDetails"
              :key="detail.label"
              :class="$style.appDetails__item"
            >
              <span :class="$style.appDetailsTitle">{{ detail.label }}</span>
              <span>{{ detail.content }}</span>
            </li>
            <li :class="$style.appDetailsLicenses">
              <div>
                <span :class="$style.appDetailsTitle">Licenses</span>
                <span>{{ getLicenses }}</span>
              </div>
              <div>
                <FlatActionButton
                  :action="handleReclaimLicenses"
                  :disabled="isReclaimLicensesDisabled"
                  :text="getReclaimLicensesBtnText"
                />
                <IconTooltip>
                  JumpCloud can reclaim licenses from deleted devices and from unknown devices.
                  Clicking reclaim licenses frees up these types of licenses so that you can use
                  them to assign apps to other managed devices. If clicking reclaim licenses didn’t
                  retrieve additional licenses, you might need to purchase additional licenses from
                  <ExternalLink
                    href="https://business.apple.com/"
                    :showExternalLinkIcon="false"
                    text="ABM"
                  /> or
                  <ExternalLink
                    href="https://school.apple.com/"
                    :showExternalLinkIcon="false"
                    text="ASM"
                  />. See
                  <ExternalLink
                    :href="KbArticleUrls.settingAppleVpp"
                    :showExternalLinkIcon="false"
                    text="Setting Up Apple's VPP"
                  />.
                </IconTooltip>
              </div>
            </li>
          </ul>
          <div :class="$style.applicationDetails">
            <span :class="$style.appDetailsTitle">Application Details</span>
            <Checkbox
              v-model="isConfigEnabled"
              label="Supply configuration"
            />
            <div
              v-show="isConfigEnabled"
              :class="$style.fullWidthTextArea"
            >
              <Textarea
                v-model="appConfiguration"
                :isFullWidth="true"
                :resizeHorizontal="false"
                :resizeVertical="false"
              />
            </div>
          </div>
          <div>
            <span :class="$style.appDetailsTitle">Description</span>
            <ReadMore
              :class="$style.appDescriptionText"
              :limit="300"
              :text="getDescription"
            />
          </div>
        </div>
      </div>
      <div :class="$style.legalFooter">
        <a
          :href="getAppUrl"
          rel="noopener noreferrer"
          target="_blank"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 13 13" :class="$style.newWindow" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path fill="#12849A" fill-rule="evenodd" d="M7.51 1.119a.55.55 0 00-.537.562.549.549 0 00.546.551l2.538-.023-3.656 3.76a.568.568 0 000 .787.53.53 0 00.766 0l3.655-3.76-.022 2.61a.55.55 0 00.536.562.549.549 0 00.546-.552l.035-3.971a.565.565 0 00-.159-.399.534.534 0 00-.387-.163L7.51 1.12zm-5.886.223a.549.549 0 00-.54.556v9.462a.55.55 0 00.54.557h9.74c.3 0 .541-.25.541-.557V8.577a.549.549 0 00-.54-.556.549.549 0 00-.542.556v2.226H2.165V2.455H4.6c.3 0 .542-.25.542-.557a.549.549 0 00-.542-.556H1.624z" clip-rule="evenodd"/></svg>
          view in app store
        </a>
      </div>
    </template>
  </DetailsAsideLayout>
</template>

<script>
import { Checkbox, Textarea } from '@jumpcloud/ui-components';
import { cloneDeep, get } from 'lodash';
import { mapActions, mapState } from 'vuex';
import { notify } from 'client/util';
import AsidePanelChildComponentMethodsMixin from 'client/components/AsidePanel/mixins/AsidePanelChildComponentMethodsMixin';
import AsidePanelChildComponentPropsMixin from 'client/components/AsidePanel/mixins/AsidePanelChildComponentPropsMixin';
import Constants, { ItemTypeConsts, StoreModelMap } from 'client/util/Constants';
import DetailsAsideLayout from 'client/components/AsidePanel/Details/DetailsAsideLayout.vue';
import ExternalLink from 'components/ExternalLink.vue';
import FlatActionButton from 'client/components/Buttons/FlatActionButton.vue';
import IconTooltip from 'client/components/Tooltip/IconTooltip.vue';
import KbArticleUrls from 'client/util/Constants/KbArticleUrls';
import ReadMore from 'client/components/ReadMore/ReadMore.vue';

const { error, success } = Constants;

const softwareAppsModel = StoreModelMap[ItemTypeConsts.softwareApps];

export default {
  name: 'VppSoftwareAppsDetails',

  components: {
    Checkbox,
    DetailsAsideLayout,
    ExternalLink,
    FlatActionButton,
    IconTooltip,
    ReadMore,
    Textarea,
  },

  mixins: [
    AsidePanelChildComponentMethodsMixin,
    AsidePanelChildComponentPropsMixin,
  ],

  data: () => ({
    isReclaimLicensesDisabled: false,
    KbArticleUrls,
  }),

  computed: {
    ...mapState(softwareAppsModel, {
      item: state => state.item,
    }),

    appConfiguration: {
      get() {
        return get(this.getAppleVppData, 'appConfiguration', '');
      },
      set(value) {
        const updatedSettings = cloneDeep(this.item.settings);
        updatedSettings[0].appleVpp.appConfiguration = value;
        this.setItemField({ key: 'settings', value: updatedSettings });
      },
    },

    appDetails() {
      return [
        {
          label: 'Version',
          content: get(this.getAppleVppData, 'details.offers[0].version.display', ''),
        },
        {
          label: 'Price',
          content: get(this.getAppleVppData, 'details.offers[0].priceFormatted', ''),
        },
        {
          label: 'Supported Devices',
          content: this.getFormattedColumnText,
        },
      ];
    },

    getAppUrl() {
      return get(this.getAppleVppData, 'details.url', '');
    },

    getAppleVppData() {
      return this.item ? get(this.item, 'settings[0].appleVpp', {}) : {};
    },

    getDescription() {
      return get(this.getAppleVppData, 'details.description.standard', '');
    },

    getFormattedColumnText() {
      // this method converts the all capitalized supported Apple Device array
      // we get from the api and formats them properly for display.
      const supportedArray = get(this.getAppleVppData, 'supportedDeviceFamilies', false);
      if (!supportedArray) {
        return '';
      }
      const formattedArray = supportedArray.map(
        (el) => {
          if (el[0] !== 'I') {
            return el[0].toUpperCase() + el.slice(1).toLowerCase();
          }
          return el[0].toLowerCase() + el[1].toUpperCase() + el.slice(2).toLowerCase();
        },
      ).join(', ');
      return formattedArray;
    },

    getLicenses() {
      const totalLicenses = get(this.getAppleVppData, 'totalLicenses', '0');
      const availableLicenses = get(this.getAppleVppData, 'availableLicenses', '0');

      return `${availableLicenses} of ${totalLicenses} available`;
    },

    getReclaimLicensesBtnText() {
      return this.isReclaimLicensesDisabled ? 'reclaiming licenses' : 'reclaim licenses';
    },

    isConfigEnabled: {
      get() {
        return get(this.getAppleVppData, 'isConfigEnabled', false);
      },
      set(value) {
        const updatedSettings = cloneDeep(this.item.settings);
        updatedSettings[0].appleVpp.isConfigEnabled = value;
        // if checkbox is checked but textarea is blank,
        // save blank string to state, as blank configurations are allowed
        if (this.appConfiguration === '') {
          updatedSettings[0].appleVpp.appConfiguration = '';
        }
        this.setItemField({ key: 'settings', value: updatedSettings });
      },
    },
  },

  beforeCreate() {
    this.$options.methods.asidePanelChildComponentHandleBeforeCreate(this.$options);
  },

  methods: {
    ...mapActions(softwareAppsModel, ['reclaimLicenses']),

    handleReclaimLicenses() {
      this.isReclaimLicensesDisabled = true;

      this.reclaimLicenses()
        .then(({ reclaimedLicenses }) => {
          notify(success, `JumpCloud was able to reclaim ${reclaimedLicenses} licenses, which you can now use to assign apps to other devices.`);
        })
        .catch(() => {
          notify(error, 'You cannot reclaim any licenses for this app at this time. You might need to purchase additional licenses or another issue is currently preventing this process from completing. Please try again later.');
        })
        .finally(() => {
          this.isReclaimLicensesDisabled = false;
        });
    },
  },
};
</script>

<style module>
@import '../css/sections.css';

@value (max-width-medium, max-width-xx-large) from '~css/breakpoints.css';

.appDetails {
  display: grid;
  grid-template-columns: 50% 50%;
  list-style: none;
  margin-bottom: 5px;
  padding: 0;
  row-gap: 2em;
}

.appDescriptionText {
  white-space: pre-line;
}

.appDetails__item {
  padding-bottom: 20px;
  width: 50%;
}

.appDetailsLicenses {
  column-gap: 1.5em;
  display: flex;
  justify-content: space-between;
  padding-bottom: 20px;
  width: 35%;
}

.appDetailsTitle {
  display: block;
  font-weight: 700;
}

.applicationDetails {
  padding-bottom: 1.54rem;
}

.detailsSection {
  display: flex;
  flex-direction: column;
}

.fullWidthTextArea {
  margin-top: 1rem;
}

.legalFooter {
  font-size: 0.9rem;
  padding-top: 11px;
}

.legalFooter a {
  margin-left: 3px;
}

.newWindow {
  margin-right: 3px;
  vertical-align: middle;
  width: 12px;
}

@media (max-width: max-width-medium) {
  .appDetails {
    grid-template-columns: 100%;
  }
}

@media (min-width: max-width-xx-large) {
  .appDetails {
    grid-template-columns: repeat(4, 25%);
  }
}
</style>
